/**
 * Why a loan tape run failed, in words an analyst can act on (DOP-627).
 *
 * The backend stores the extractor's own message with the run
 * ("GrainViolation: canonical_loan declares grain key 'loan_id' but it is
 * unmapped for deal 'train'…"). That names the fault exactly but is written
 * for whoever built the extractor. The known shapes are turned into which
 * file to fix and how; anything else says so plainly and keeps the original
 * one click away, so nothing the backend said is lost.
 */

import type { SchemaExtractorPayload } from "../SchemaExtractorForm";

export interface RunFailure {
	/** One or two sentences: what went wrong and what to do. */
	summary: string;
	/** The backend's own message, for "Show details". */
	detail: string | null;
}

/** "loan_id" → "Loan ID". */
const fieldLabel = (key: string): string =>
	key
		.split("_")
		.map((part) => (part === "id" ? "ID" : part.charAt(0).toUpperCase() + part.slice(1)))
		.join(" ");

/** "canonical_loan" → "loan". */
const entityLabel = (name: string): string =>
	name.replace(/^canonical_/, "").replace(/_/g, " ");

const GRAIN = /grain key '([^']+)' but it is unmapped for deal '([^']+)'/;
const NOT_XLSX = /(.+?) is not an \.xlsx workbook/;
const NOT_FOUND = /Could not find workbook (.+?) in deal '([^']+)'/;
const ENTITY = /(canonical_[a-z_]+) declares grain key/;

export const explainRunFailure = (
	error: string | null,
	config: SchemaExtractorPayload | null,
): RunFailure => {
	const detail = error?.replace(/^Schema extraction failed:\s*/, "") ?? null;
	if (!detail) {
		return {
			summary: "This run failed before it produced a loan tape.",
			detail: null,
		};
	}

	const grain = GRAIN.exec(detail);
	if (grain) {
		const [, key, deal] = grain;
		const field = fieldLabel(key);
		const entity = entityLabel(ENTITY.exec(detail)?.[1] ?? "canonical_loan");
		const onExample = deal === "train";
		const file = onExample
			? (config?.training_source.filename ?? "the example's raw file")
			: (config?.new_source.filename ?? "the new file");
		return {
			summary: onExample
				? `Doppler couldn't find a ${field} for each ${entity} in the example's raw file, ${file}, so it couldn't learn how your tape is built. Use a raw file where the ${entity} records carry a ${field} (or a column that links them to one), then run it again.`
				: `Doppler couldn't find a ${field} for each ${entity} in the new file, ${file}. Check it has a ${field} column like the example's raw file, then run it again.`,
			detail,
		};
	}

	const notXlsx = NOT_XLSX.exec(detail);
	if (notXlsx) {
		return {
			summary: `${notXlsx[1]} isn't an Excel (.xlsx) file. Choose an .xlsx file in How it's set up, then run it again.`,
			detail,
		};
	}

	const notFound = NOT_FOUND.exec(detail);
	if (notFound) {
		return {
			summary: `${notFound[1]} is no longer in ${notFound[2]}. It may have been deleted or moved. Choose it again in How it's set up.`,
			detail,
		};
	}

	return {
		summary:
			"Something went wrong on Doppler's side while building the tape. Try again; if it fails the same way, send us the details.",
		detail,
	};
};
